import { useEffect, useRef } from 'react';

/**
 * A popup that closes the two ways every popup is expected to close: Escape, and a
 * click anywhere else.
 *
 * <p>The job card's ⋮ menu had neither. It opened on a click and closed only on a
 * second click of the same button or on choosing an item, so a menu opened by accident
 * stayed open over the board — Escape did nothing, clicking away did nothing, and it
 * followed the card as the lane scrolled. Every popup gets this rather than each one
 * remembering to.
 *
 * <p>⚠️ `pointerdown`, not `click`. A menu item that re-renders the board on click can
 * unmount the target before the click event finishes, and a `click`-based check then
 * sees a node no longer in the document and reads it as "outside" — closing the menu
 * before the item's own handler runs. `pointerdown` fires first, on the element still
 * in the tree.
 *
 * @param {boolean} open whether the popup is showing; listeners are only bound then
 * @param {() => void} onClose
 * @returns {React.RefObject} put this on the element that counts as "inside"
 */
export default function useDismissable(open, onClose) {
    const ref = useRef(null);
    // Kept in a ref so a caller passing an inline arrow does not rebind the listeners
    // on every render — which would drop the pointerdown mid-gesture.
    const close = useRef(onClose);
    close.current = onClose;

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event) => {
            if (!ref.current || ref.current.contains(event.target)) return;
            close.current();
        };
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            // Only this popup: an Escape inside a modal that also listens must not be
            // consumed twice. The caller unmounts on close, so the listener goes with it.
            event.stopPropagation();
            close.current();
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open]);

    return ref;
}
